// 거래소 클라(docs/PARTY-TRADE-PRD.md 1.1·4장). API 호출 + 클라 장비 장부와 맞추기("서버 먼저, 클라는 따라 맞춘다").
// - 맡기기(장비): 서버가 보관함에 넣으면 → 장부 mkOut(mk:<idem>)으로 가방에서 뺀다.
// - 맡기기(골드): 가방 골드를 먼저 확인 → 서버 금고 +d → 장부 mkGold(−d, mk:<idem>) → ack-gold.
// - 맞추기(reconcile): ack 안 된 장비·골드 맡기기만 맡긴 캐릭터에서 같은 연산 id(mk:<idem>)로 마저 뺀 뒤 ack한다.
//   (지금 서버 보유 목록으로 가방을 지우지 않는다 — 늦게 온 /me로 정상 회수한 장비를 지우던 문제, PR #246 Codex P2)
//   연산 id가 같아 두 번 적용되지 않는다.
// 장부 쓰기는 게임 쪽(src/game/marketApply.ts)이 MarketLedger로 넘긴다. 이 파일은 DOM을 모른다.
import type { GearItem } from '../shared/gear';
import type { ListingView } from '../server/market';

export type MarketResult<T> = { ok: true; data: T } | { ok: false; status: number; code: string; reason?: string };

export interface MarketLedger {
  /** 가방(장착 제외) 장비 */
  bagItems(): readonly GearItem[];
  /** 지금 쓸 수 있는 골드 */
  gold(): number;
  /** 가방에서 뺀다(mkOut). 저장 확정까지 기다린다 */
  out(uid: string, opId: string): Promise<boolean>;
  /** 이 캐릭터가 그 장비를 아직 가지고 있을 수 있나(가방·장착). 저장이 확인되지 않았거나 대기 연산이 있으면 true(보수적) */
  hasItem(uid: string): boolean;
  /** 골드 지출(mkGold −amount) */
  goldOut(amount: number, opId: string): Promise<boolean>;
}

export interface MarketMe {
  wallet: number; vault: GearItem[]; listings: ListingView[]; bids: ListingView[];
  pendingGold: { idem: string; gold: number; ch?: string }[]; pendingDeposits?: { idem: string; uid: string; ch?: string }[]; daily?: { claimed: boolean; streak: number; next: number }; held: string[]; heldRec?: { uid: string; rec: string }[]; canSell: boolean; clientItems: boolean; goldDeposit?: boolean;
}
export interface ListingsPage { listings: ListingView[]; more: boolean; page: number }
export interface PriceView { def: string; grade: string; count: number; median: number | null; min: number | null; max: number | null }

export type Fetcher = (path: string, init: { method: string; credentials: 'same-origin'; headers: Record<string, string>; body?: string }) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

/** 요청 id(16바이트 base64url) */
export function newIdem(): string {
  const b = crypto.getRandomValues(new Uint8Array(16));
  let s = '';
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export class MarketClient {
  constructor(private readonly ledger: MarketLedger, private readonly f: Fetcher = (p, i) => fetch(p, i), private readonly characterId: () => string = () => 'legacy') {}

  private async api<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<MarketResult<T>> {
    try {
      const res = await this.f(path, {
        method, credentials: 'same-origin',
        headers: body === undefined ? {} : { 'content-type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const data = await res.json().catch(() => null) as (T & { error?: string; reason?: string }) | null;
      if (res.ok && data) return { ok: true, data };
      return { ok: false, status: res.status, code: data?.error ?? 'server', reason: data?.reason };
    } catch { return { ok: false, status: 0, code: 'network' }; }
  }

  /** 지금 캐릭터 기준(골드 맡기기 복구·출석은 캐릭터별) */
  me(): Promise<MarketResult<MarketMe>> { return this.api('GET', '/api/market/me?characterId=' + encodeURIComponent(this.characterId())); }
  /** 출석 금고 지급(캐릭터·KST 하루 1번) */
  daily(): Promise<MarketResult<{ ok: true; replay: boolean; gold: number; streak: number; wallet: number }>> {
    return this.api('POST', '/api/market/daily', { characterId: this.characterId() });
  }
  listings(q: { slot?: string; grade?: string; sort?: 'ends' | 'price' | 'buyout'; page?: number } = {}): Promise<MarketResult<ListingsPage>> {
    const p = new URLSearchParams();
    if (q.slot) p.set('slot', q.slot);
    if (q.grade) p.set('grade', q.grade);
    if (q.sort) p.set('sort', q.sort);
    if (q.page) p.set('page', String(q.page));
    const s = p.toString();
    return this.api('GET', '/api/market/listings' + (s ? '?' + s : ''));
  }
  price(def: string, grade: string): Promise<MarketResult<PriceView>> {
    return this.api('GET', '/api/market/price?def=' + encodeURIComponent(def) + '&grade=' + encodeURIComponent(grade));
  }

  /** 서버가 맡고 있는데 가방에 남은 사본을 빼고, ack 안 된 골드 맡기기를 마저 처리한다. 고친 수를 돌려준다 */
  async reconcile(me: MarketMe): Promise<number> {
    let n = 0;
    const ch = this.characterId();
    // 장비 맡기기: 맡긴 캐릭터에서 같은 연산 id(mk:<idem>)로 마저 빼고, 빠졌거나(이미 없음) 저장되면 ack — ack 전에는 서버가 등록·꺼내기를 막는다(PR #246 Codex P1)
    for (const p of me.pendingDeposits ?? []) {
      if ((p.ch ?? 'legacy') !== ch) continue;
      const gone = await this.ledger.out(p.uid, 'mk:' + p.idem) || !this.ledger.hasItem(p.uid);
      if (gone) { await this.api('POST', '/api/market/ack-deposit', { idem: p.idem, characterId: ch }); n++; }
    }
    // 골드 맡기기는 맡긴 캐릭터에서만 마저 뺀다(다른 캐릭터 장부에서 두 번 빠지지 않게 — PR #246 Codex P1)
    for (const p of me.pendingGold) {
      if ((p.ch ?? 'legacy') !== ch) continue;
      if (await this.ledger.goldOut(p.gold, 'mk:' + p.idem)) { await this.api('POST', '/api/market/ack-gold', { idem: p.idem, characterId: ch }); n++; }
    }
    return n;
  }

  /** 장비 맡기기: 서버 보관함 → 가방에서 뺀다 */
  async deposit(item: GearItem): Promise<MarketResult<{ ok: true; replay: boolean; uid: string }>> {
    const idem = newIdem(), characterId = this.characterId();
    const r = await this.api<{ ok: true; replay: boolean; uid: string }>('POST', '/api/market/deposit', { item, idem, characterId });
    if (r.ok && (await this.ledger.out(item.uid, 'mk:' + idem) || !this.ledger.hasItem(item.uid))) await this.api('POST', '/api/market/ack-deposit', { idem, characterId });
    return r;
  }
  /** 보관함 → 우편(받기는 우편함) */
  withdraw(uid: string): Promise<MarketResult<{ ok: true; replay: boolean }>> { return this.api('POST', '/api/market/withdraw', { uid, idem: newIdem() }); }

  /** 골드 맡기기: 가방 골드 확인 → 금고 +amount → 가방 −amount → ack */
  async depositGold(amount: number): Promise<MarketResult<{ ok: true; wallet: number }>> {
    if (!Number.isSafeInteger(amount) || amount <= 0) return { ok: false, status: 400, code: 'bad-request' };
    if (this.ledger.gold() < amount) return { ok: false, status: 402, code: 'insufficient' };
    const idem = newIdem();
    const characterId = this.characterId();
    const r = await this.api<{ ok: true; wallet: number }>('POST', '/api/market/gold', { d: amount, idem, characterId });
    if (r.ok && await this.ledger.goldOut(amount, 'mk:' + idem)) await this.api('POST', '/api/market/ack-gold', { idem, characterId });
    return r;
  }
  /** 금고 출금(골드 우편) */
  withdrawGold(amount: number): Promise<MarketResult<{ ok: true; wallet: number }>> { return this.api('POST', '/api/market/gold', { d: -amount, idem: newIdem() }); }

  list(uid: string, start: number, buyout: number, hours: number): Promise<MarketResult<{ ok: true; id: string }>> {
    return this.api('POST', '/api/market/list', { uid, start, buyout, hours, idem: newIdem() });
  }
  bid(listingId: string, amount: number): Promise<MarketResult<{ ok: true; wallet: number }>> { return this.api('POST', '/api/market/bid', { listingId, amount, idem: newIdem() }); }
  buyout(listingId: string): Promise<MarketResult<{ ok: true; wallet: number }>> { return this.api('POST', '/api/market/buyout', { listingId, idem: newIdem() }); }
  cancel(listingId: string): Promise<MarketResult<{ ok: true }>> { return this.api('POST', '/api/market/cancel', { listingId, idem: newIdem() }); }
}

/** 오류 코드 → 한국어 한 줄 */
export const MARKET_ERROR_TEXT: Readonly<Record<string, string>> = {
  'market-disabled': '거래소가 아직 열리지 않았어요.',
  'no-session': '계정 저장 후 이용할 수 있어요.',
  'link-required': 'Google 계정을 연결해야 판매·입찰할 수 있어요.',
  'not-eligible': '새 계정은 조금 뒤부터 거래할 수 있어요(판매·입찰 3일, 구매 1일).',
  'item-invalid': '거래할 수 없는 장비예요.',
  'not-tradable': '거래 가능한 장비가 아니에요(희귀 이상, 거래소 인증 장비만).',
  'item-owned': '이미 거래소에 있거나 다른 사람 소유인 장비예요.',
  'item-modified': '강화하거나 보석을 바꾼 장비는 아직 거래할 수 없어요.',
  'deposit-pending': '맡긴 장비를 가방에서 정리하는 중이에요. 잠시 뒤 다시 시도해 주세요.',
  'deposit-closed': '골드 맡기기는 아직 열리지 않았어요. 판매 대금으로 거래할 수 있어요.',
  'price-range': '가격이 허용 범위를 벗어났어요.',
  limit: '오늘 한도에 닿았어요.',
  'own-listing': '내 물건에는 입찰·구매할 수 없어요.',
  closed: '이미 끝난 거래예요.',
  'bid-low': '입찰가가 낮아요.',
  'use-buyout': '즉시 구매가 이상이면 즉시 구매를 눌러 주세요.',
  outbid: '다른 사람이 먼저 입찰했어요. 다시 확인해 주세요.',
  insufficient: '거래 금고 골드가 모자라요.',
  'has-bids': '입찰이 있어 취소할 수 없어요.',
  rate: '너무 빨라요. 잠시 뒤 다시 시도해 주세요.',
  network: '연결이 끊겼어요.',
};
